import React, { useCallback, useEffect, useState } from "react";
import { useStores } from "netbank-shared/src/hooks";
import { Button, Lottie } from "~views/shared";
import styles from "./Tabs.scss";
import rootStyles from "../../pages/Root.scss";
import loadingAnimationData from "~assets/lottie/loading.json";
import classNames from "classnames";

interface TabProps<TKey extends string> {
  items: {
    key: TKey;
    label: string;
    children: React.ReactNode;
  }[];
  activePanel: TKey;
  onChange?: (key: TKey) => void;
  loading?: boolean;
  extra?: React.ReactNode;
  footer?: React.ReactNode;
}

export const Tabs = <TKey extends string>(props: TabProps<TKey>) => {
  const { uiStore } = useStores();
  const refList = React.useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState<TKey>(props.activePanel);

  const onKeyDown = useCallback((e: React.KeyboardEvent) => {
    const list = refList.current;
    if (!list) return;

    const tabs = Array.from<HTMLElement>(list.querySelectorAll('[role="tab"]:not([disabled])'));
    const index = tabs.indexOf(document.activeElement as HTMLElement);

    if (index < 0) return;

    switch (e.key) {
      case "ArrowUp":
      case "ArrowLeft": {
        const next = (index - 1 + tabs.length) % tabs.length;
        tabs[next]?.focus();
        break;
      }
      case "ArrowDown":
      case "ArrowRight": {
        const next = (index + 1 + tabs.length) % tabs.length;
        tabs[next]?.focus();
        break;
      }
      default:
        break;
    }
  }, []);

  useEffect(() => {
    setActiveTab(props.activePanel);
  }, [props.activePanel]);

  const messageClasses = [rootStyles.noBorder, rootStyles.accordion];

  if (uiStore.isMobile) {
    messageClasses.push(rootStyles.noPadding);
  }

  const renderTabs = () => {
    return (
      <div className={styles.tabs} role="tablist">
        {props.items.map((item) => (
          <Button
            key={item.key}
            role="tab"
            type="secondary"
            title={item.label}
            id={`tab-${item.key}`}
            aria-controls={`panel-${item.key}`}
            aria-selected={activeTab === item.key}
            className={classNames(styles.tab, {
              [styles.active]: activeTab === item.key,
            })}
            onClick={() => {
              setActiveTab(item.key);
              props.onChange?.(item.key);
            }}
          />
        ))}
      </div>
    );
  };

  return (
    <div ref={refList} onKeyDown={onKeyDown}>
      {uiStore.isSm && <div className={styles.outsideTabsContainer}>{renderTabs()}</div>}
      <div className={styles.tabsWrapper}>
        <div className={styles.tabsHeader}>
          {!uiStore.isSm && renderTabs()}
          {props.extra}
        </div>
        {props.loading ? (
          <div className={styles.loadingRow}>
            <Lottie
              options={{
                animationData: loadingAnimationData,
              }}
              height={100}
              width={100}
            />
          </div>
        ) : (
          <>
            <div role="tabpanel" id={`panel-${activeTab}`} aria-labelledby={`tab-${activeTab}`}>
              {props.items.find((item) => item.key === activeTab)?.children}
            </div>
            {props.footer}
          </>
        )}
      </div>
    </div>
  );
};
